import { cloneElement, isValidElement, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export default function Tooltip({ content, children, placement = 'bottom' }) {
  const enabled = !!content;

  const anchorRef = useRef(null);
  const rafRef = useRef(null);

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipMounted, setTooltipMounted] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ left: 0, top: 0 });

  const updateTooltipPos = () => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setTooltipPos({
      left: rect.left + rect.width / 2,
      top: placement === 'top' ? rect.top - 10 : rect.bottom + 10,
    });
  };

  useEffect(() => {
    if (!tooltipMounted) return;
    updateTooltipPos();

    const handleScroll = () => updateTooltipPos();
    const handleResize = () => updateTooltipPos();

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [tooltipMounted, placement]);

  useEffect(() => {
    if (tooltipOpen) {
      setTooltipMounted(true);
      return;
    }

    if (!tooltipMounted) return;
    const timeoutId = window.setTimeout(() => setTooltipMounted(false), 140);
    return () => window.clearTimeout(timeoutId);
  }, [tooltipOpen, tooltipMounted]);

  useEffect(() => {
    return () => {
      if (rafRef.current != null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, []);

  const open = () => {
    if (!enabled) return;

    setTooltipMounted(true);
    setTooltipOpen(false);
    updateTooltipPos();

    if (rafRef.current != null) {
      window.cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = window.requestAnimationFrame(() => {
      setTooltipOpen(true);
      rafRef.current = null;
    });
  };

  const close = () => {
    if (rafRef.current != null) {
      window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    setTooltipOpen(false);
  };

  if (!children) return null;
  if (!enabled || !isValidElement(children)) return <>{children}</>;

  const existingRef = children.ref;
  const setRef = (node) => {
    anchorRef.current = node;

    if (!existingRef) return;
    if (typeof existingRef === 'function') {
      existingRef(node);
    } else {
      try {
        existingRef.current = node;
      } catch {
        // ignore
      }
    }
  };

  return (
    <>
      {enabled &&
        tooltipMounted &&
        typeof document !== 'undefined' &&
        createPortal(
          <span
            className={`df_button_tooltip ${
              placement === 'top' ? 'df_button_tooltip_top' : ''
            } ${tooltipOpen ? 'df_button_tooltip_visible' : ''}`}
            role='tooltip'
            style={{ left: tooltipPos.left, top: tooltipPos.top }}
          >
            {content}
          </span>,
          document.body,
        )}

      {enabled
        ? (
            // clone the child to inject tooltip behaviors
            // eslint-disable-next-line react/no-children-prop
            cloneElement(children, {
              ref: setRef,
              onMouseEnter: (e) => {
                children.props?.onMouseEnter?.(e);
                open();
              },
              onMouseLeave: (e) => {
                children.props?.onMouseLeave?.(e);
                close();
              },
              onFocus: (e) => {
                children.props?.onFocus?.(e);
                open();
              },
              onBlur: (e) => {
                children.props?.onBlur?.(e);
                close();
              },
            })
          )
        : children}
    </>
  );
}
